import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { 
  GamepadIcon, 
  BookOpenIcon, 
  UsersIcon, 
  PuzzleIcon,
  TrophyIcon,
  VideoIcon 
} from "lucide-react";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from "@/components/ui/card";
import { MinigamesView } from "@/components/games/MinigamesView";
import { Button } from "@/components/ui/button";
import { MarketSimulator } from "@/components/games/MarketSimulator";
import { BudgetGridlock } from "@/components/games/financial-puzzles/BudgetGridlock";
import { FinancialPuzzlesView } from "@/components/games/financial-puzzles/FinancialPuzzlesView";

interface GameCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
}

const Play = () => {
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const gameCategories: GameCategory[] = [
    {
      id: "minigames",
      title: "Quick Minigames",
      description: "Fast-paced games to test your financial knowledge",
      icon: <GamepadIcon className="w-6 h-6" />
    },
    {
      id: "story",
      title: "Story Mode",
      description: "Follow a character's journey to financial success",
      icon: <BookOpenIcon className="w-6 h-6" />,
      comingSoon: true
    },
    {
      id: "multiplayer",
      title: "Multiplayer Challenges",
      description: "Compete with friends in financial battles",
      icon: <UsersIcon className="w-6 h-6" />,
      comingSoon: true
    },
    {
      id: "simulators",
      title: "Market Simulators",
      description: "Practice trading in a risk-free environment",
      icon: <VideoIcon className="w-6 h-6" />
    },
    {
      id: "puzzle",
      title: "Financial Puzzles",
      description: "Test your financial knowledge with challenging puzzles",
      icon: <PuzzleIcon className="w-6 h-6" />
    },
    {
      id: "achievements",
      title: "Achievement Center",
      description: "Track your progress and earn rewards",
      icon: <TrophyIcon className="w-6 h-6" />,
      comingSoon: true
    }
  ];

  const handleCategoryClick = (category: GameCategory) => {
    if (category.comingSoon) {
      toast({
        title: "Coming Soon!",
        description: `${category.title} will be available soon. Stay tuned!`,
        variant: "default"
      });
      return;
    }

    setSelectedCategory(category.id);
  };

  if (selectedCategory === "minigames") {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold text-gray-900">Game Center</h1>
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="hover:bg-secondary/20"
          >
            Back to Categories
          </Button>
        </div>
        <MinigamesView />
      </div>
    );
  }

  if (selectedCategory === "simulators") {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold text-gray-900">Market Simulator</h1>
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="hover:bg-secondary/20"
          >
            Back to Categories
          </Button>
        </div>
        <MarketSimulator />
      </div>
    );
  }

  if (selectedCategory === "puzzle") {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold text-gray-900">Financial Puzzles</h1>
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="hover:bg-secondary/20"
          >
            Back to Categories
          </Button>
        </div>
        <FinancialPuzzlesView />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">Game Center</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gameCategories.map((category) => (
          <Card 
            key={category.id}
            className={`
              group cursor-pointer transition-all duration-300 
              hover:shadow-lg hover:scale-[1.02] 
              ${category.comingSoon ? 'opacity-75' : ''}
            `}
            onClick={() => handleCategoryClick(category)}
          >
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-secondary/10 group-hover:bg-secondary/20 transition-colors">
                  <div className="text-secondary group-hover:scale-110 transition-transform">
                    {category.icon}
                  </div>
                </div>
                <div>
                  <CardTitle className="text-xl">
                    {category.title}
                    {category.comingSoon && (
                      <span className="ml-2 text-sm text-muted-foreground">(Coming Soon)</span>
                    )}
                  </CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Additional content can be added here later */}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Play;
